/**
 * Shown in the shift drawer when the person rostered on has said they cannot
 * work it.
 *
 * The boss decides, always. Staff put their hand up and this panel lists them;
 * nobody is moved onto a shift by the act of volunteering. Picking somebody
 * here reassigns the shift and clears the cover flag in one step, because doing
 * those separately is how a shift ends up assigned but still flagged.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hand, UserCheck } from 'lucide-react';
import { api, qk, type Shift } from '../lib/api.ts';
import { Avatar, Spinner } from '../components/Bits.tsx';
import { fmtDateLong } from '../lib/time.ts';

export function CoverPanel({
  slug,
  shift,
  onAssign,
  assigning,
}: {
  slug: string;
  shift: Shift;
  /** Reassigns the shift and clears the flag, handled by the roster page. */
  onAssign: (userId: string) => void;
  assigning: boolean;
}) {
  const qc = useQueryClient();
  const claims = useQuery({
    queryKey: qk.shiftClaims(slug, shift.id),
    queryFn: () => api.shiftClaims(slug, shift.id),
    enabled: shift.needsCover || shift.userId === null,
  });

  const clear = useMutation({
    mutationFn: () => api.cancelCover(slug, shift.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });

  if (!shift.needsCover) return null;
  const items = claims.data?.items ?? [];

  return (
    <div
      className="mb-5 rounded-card border p-3.5"
      style={{ background: 'var(--sp-warn-soft)', borderColor: 'var(--sp-warn-border)' }}
    >
      <div className="flex gap-2.5">
        <Hand size={17} aria-hidden="true" className="mt-0.5 shrink-0" style={{ color: 'var(--sp-warn-strong)' }} />
        <div className="min-w-0 flex-1 text-[13px] leading-relaxed">
          <strong className="block text-ink">
            This shift needs cover on {fmtDateLong(shift.date)}.
          </strong>
          {shift.coverReason ? (
            <span className="block text-muted">Reason given: {shift.coverReason}</span>
          ) : null}
          <span className="mt-1 block text-muted">
            They are still rostered until you put somebody else on, so the shift is not left uncovered by
            accident.
          </span>
        </div>
      </div>

      <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--sp-warn-border)' }}>
        {claims.isLoading ? (
          <Spinner size={15} label="Loading who offered" />
        ) : items.length === 0 ? (
          <p className="text-[12.5px] text-muted">
            Nobody has offered to take it yet. The rest of the team has been asked.
          </p>
        ) : (
          <>
            <p className="mb-2 text-[12.5px] font-semibold text-ink">
              {items.length === 1 ? '1 person can work it' : `${items.length} people can work it`}
            </p>
            <ul className="grid gap-1.5">
              {items.map((c) => (
                <li key={c.userId} className="flex items-center gap-2.5">
                  <Avatar name={c.name} size={26} />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">{c.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={assigning}
                    onClick={() => onAssign(c.userId)}
                  >
                    <UserCheck size={14} aria-hidden="true" />
                    Put them on
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        <button
          type="button"
          className="btn btn-sm mt-3"
          disabled={clear.isPending}
          onClick={() => clear.mutate()}
        >
          {clear.isPending ? <Spinner size={14} label="Clearing" /> : 'Sorted, clear the flag'}
        </button>
      </div>
    </div>
  );
}
